import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse, Flex, Select, Spinner, Text } from "@chakra-ui/react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  getMyBdcApi,
  getMyBdcRelativesApi,
  getBdcPeriodListApi,
} from "../../api/finance.bdc.api";
import { formatVnd } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#E1E1E1";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const SUBTLE = "#51607B";
const WARNING = "#F79D0D";
const SUCCESS = "#0BA859";
const LIMIT = 20;

// Thuế TNCN hiển thị: dùng giá trị đã set, hoặc mặc định 10% × Số tiền tạm giữ.
const displayTax = (taxTNCN, amountHeld) =>
  taxTNCN != null ? Number(taxTNCN) : 0.1 * Number(amountHeld || 0);

const SumRow = ({ label, value, color }) => (
  <Flex justify="space-between" align="flex-start" gap="12px">
    <Text fontSize="13px" color={SUBTLE} flex="0.5">{label}</Text>
    <Text fontSize="14px" fontWeight={600} color={color || TEXT} flex="0.5" textAlign="right" noOfLines={2}>{value}</Text>
  </Flex>
);

const Chevron = ({ open }) => (
  <Box as={open ? FiChevronDown : FiChevronRight} fontSize="18px" color={MUTED} flexShrink={0} />
);

// ── Khối 1: thẻ tổng gộp (stat cards, không icon) ──────────────────
const TotalCard = ({ totals }) => {
  const cards = [
    { label: "Số tiền tạm giữ", value: totals.amountHeld, color: APP.primary200 },
    { label: "Thuế TNCN", value: totals.taxTNCN, color: TEXT },
    { label: "Số tiền đã trả", value: totals.amountPaid, color: TEXT },
    { label: "Số tiền còn lại", value: totals.amountActualRemaining, color: TEXT },
  ];
  return (
    <Flex wrap="wrap" justify="space-between" gap="10px">
      {cards.map((c) => (
        <Box key={c.label} w="48.5%" bg="white" border="1px solid" borderColor="#E5E9F0" borderRadius="12px" px="12px" py="14px">
          <Text fontSize="13px" color={SUBTLE} noOfLines={1} mb="6px">{c.label}</Text>
          <Text fontSize="14px" fontWeight={600} color={c.color} noOfLines={1}>{formatVnd(c.value)}</Text>
        </Box>
      ))}
    </Flex>
  );
};

// ── Khối 2: thẻ chi tiết từng thành viên (collapse) ────────────────
const MemberCard = ({ member, open, onToggle }) => {
  const statusText = member.isBrokerDebtCertificate ? "Có CCMG" : "Không có CCMG";
  const statusColor = member.isBrokerDebtCertificate ? SUCCESS : APP.gray;
  const tax = displayTax(member.taxTNCN, member.amountHeld);
  return (
    <Box bg="white" border="1px solid" borderColor="#E5E9F0" borderRadius="12px" overflow="hidden">
      <Flex as="button" w="100%" align="center" justify="space-between" gap="10px" p="14px" onClick={onToggle}>
        <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} flex="1" textAlign="left">
          {member.fullName || "—"}{member.code ? ` - ${member.code}` : ""}
        </Text>
        <Chevron open={open} />
      </Flex>
      <Collapse in={open} animateOpacity>
        <Box px="14px" pb="14px">
          <Box h="1px" bg={BORDER} mb="8px" />
          <Flex direction="column" gap="8px">
            <SumRow label="Trạng thái CCMG" value={statusText} color={statusColor} />
            <SumRow label="Số tiền tạm giữ" value={formatVnd(member.amountHeld || 0)} color={APP.primary200} />
            <SumRow label="Thuế TNCN" value={formatVnd(tax)} />
            <SumRow label="Số tiền đã trả" value={formatVnd(member.amountPaid || 0)} />
            <SumRow label="Số tiền còn lại" value={formatVnd(member.amountActualRemaining || 0)} />
          </Flex>
        </Box>
      </Collapse>
    </Box>
  );
};

// ── Khối 3: thẻ một đợt tạm giữ (có trường Tên) ────────────────────
const PeriodCard = ({ item, name }) => {
  const sourcePdv = !!item?.supersededByPdv;
  const sourceText = sourcePdv ? "Tính toán PDV" : "Import CCMG";
  const sourceColor = sourcePdv ? APP.primary200 : WARNING;
  return (
    <Box bg="white" border="1px solid" borderColor="#E5E9F0" borderRadius="12px" p="14px">
      <Flex align="center" justify="space-between" gap="8px">
        <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} flex="1">{item?.apartmentCode || "—"}</Text>
        <Box bg="#F1F4F9" borderRadius="6px" px="8px" py="2px">
          <Text fontSize="13px" fontWeight={500} color={sourceColor}>{sourceText}</Text>
        </Box>
      </Flex>
      <Box h="1px" bg={BORDER} my="12px" />
      <Flex direction="column" gap="8px">
        {name ? <SumRow label="Tên" value={name} /> : null}
        <SumRow label="Đợt" value={item?.period != null ? `Đợt ${item.period}` : "—"} />
        <SumRow label="Tháng/Năm" value={item?.monthYear || "—"} />
        <SumRow label="Số tiền CCMG" value={formatVnd(item?.amountHeld || 0)} color={APP.primary200} />
      </Flex>
    </Box>
  );
};

const BdcHeld = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [my, setMy] = useState(null);
  const [relatives, setRelatives] = useState([]);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [booting, setBooting] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [openIds, setOpenIds] = useState({});

  const reqId = useRef(0);
  const allIdsRef = useRef([]);
  const selRef = useRef("");

  const fetchPeriods = useCallback(async (p, mode) => {
    const ids = allIdsRef.current;
    if (!ids.length) return;
    reqId.current += 1;
    const rid = reqId.current;
    setLoading(true);
    try {
      const sel = selRef.current;
      const params = sel
        ? { employeeId: sel, page: p, limit: LIMIT }
        : { employeeIds: ids, page: p, limit: LIMIT };
      const data = await getBdcPeriodListApi(params);
      if (rid !== reqId.current) return;
      const list = data?.items || [];
      setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
      setTotalPages(data?.pagination?.totalPages || 0);
      setPage(p);
    } catch {
      if (rid === reqId.current && mode !== "append") setItems([]);
    } finally {
      if (rid === reqId.current) setLoading(false);
    }
  }, []);

  // Vào màn: nạp cá nhân + CTV rồi fetch danh sách MỘT LẦN (tránh giật/mất list)
  useEffect(() => {
    let alive = true;
    setBooting(true);
    setLoading(true);
    Promise.all([
      getMyBdcApi().catch(() => null),
      getMyBdcRelativesApi().catch(() => ({ items: [] })),
    ]).then(([myD, relD]) => {
      if (!alive) return;
      const rels = relD?.items || [];
      setMy(myD || {});
      setRelatives(rels);
      const ids = Array.from(
        new Set(
          [myD?.employeeId, ...rels.map((r) => r.employeeId)].filter(Boolean),
        ),
      );
      allIdsRef.current = ids;
      if (myD?.employeeId) setOpenIds({ [myD.employeeId]: true });
      setBooting(false);
      if (ids.length) fetchPeriods(1, "replace");
      else setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [fetchPeriods]);

  const totals = useMemo(() => {
    const list = [my, ...relatives].filter(Boolean);
    return list.reduce(
      (acc, it) => {
        acc.amountHeld += it.amountHeld || 0;
        acc.taxTNCN += displayTax(it.taxTNCN, it.amountHeld);
        acc.amountPaid += it.amountPaid || 0;
        acc.amountActualRemaining += it.amountActualRemaining || 0;
        return acc;
      },
      { amountHeld: 0, taxTNCN: 0, amountPaid: 0, amountActualRemaining: 0 },
    );
  }, [my, relatives]);

  const members = useMemo(() => {
    const rows = [];
    const seen = new Set();
    if (my?.employeeId) {
      seen.add(my.employeeId);
      rows.push(my);
    }
    relatives.forEach((r) => {
      if (!r.employeeId || seen.has(r.employeeId)) return;
      seen.add(r.employeeId);
      rows.push(r);
    });
    return rows;
  }, [my, relatives]);

  const options = useMemo(() => {
    const opts = [{ value: "", label: "Tất cả" }];
    const seen = new Set();
    if (my?.employeeId) {
      seen.add(my.employeeId);
      opts.push({ value: my.employeeId, label: my.fullName || "Cá nhân" });
    }
    relatives.forEach((r) => {
      if (!r.employeeId || seen.has(r.employeeId)) return;
      seen.add(r.employeeId);
      opts.push({ value: r.employeeId, label: r.fullName || "—" });
    });
    return opts;
  }, [my, relatives]);

  const nameMap = useMemo(() => {
    const m = {};
    if (my?.employeeId) m[my.employeeId] = my.fullName || "";
    relatives.forEach((r) => {
      if (r.employeeId) m[r.employeeId] = r.fullName || "";
    });
    return m;
  }, [my, relatives]);

  const periodList = useMemo(() => {
    const seen = new Set();
    const out = [];
    items.forEach((p) => {
      const id = p?._id ? String(p._id) : "";
      if (id && seen.has(id)) return;
      if (id) seen.add(id);
      out.push(p);
    });
    return out;
  }, [items]);

  const onSelectEmployee = (val) => {
    const id = val || "";
    setSelectedEmployeeId(id);
    selRef.current = id;
    fetchPeriods(1, "replace");
  };

  const toggleMember = (id) =>
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));

  const hasMore = totalPages ? page < totalPages : false;

  const pageBody = (
    <>
      <Box px="16px" pt="12px">
        {/* Khối 1 */}
        <TotalCard totals={totals} />

        {/* Khối 2 */}
        <Text fontSize="14px" fontWeight={600} color={TEXT} mt="16px" mb="10px">Chi tiết theo thành viên</Text>
        <Flex direction="column" gap="10px">
          {members.map((mem) => (
            <MemberCard
              key={mem.employeeId}
              member={mem}
              open={!!openIds[mem.employeeId]}
              onToggle={() => toggleMember(mem.employeeId)}
            />
          ))}
        </Flex>

        {/* Khối 3 */}
        <Flex align="center" justify="space-between" gap="12px" mt="16px" mb="12px">
          <Text fontSize="14px" fontWeight={600} color={TEXT} flexShrink={0}>Danh sách các đợt tạm giữ</Text>
          <Select
            size="sm"
            maxW="55%"
            borderRadius="8px"
            value={selectedEmployeeId}
            onChange={(e) => onSelectEmployee(e.target.value)}
          >
            {options.map((o) => (
              <option key={o.value || "all"} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Flex>

        {loading && !periodList.length ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : periodList.length === 0 && !booting ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="24px">Chưa có dữ liệu lịch sử CCMG</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {periodList.map((it, i) => (
              <PeriodCard key={it._id || i} item={it} name={nameMap[it?.employeeId] || it?.employeeObject?.fullName || ""} />
            ))}
            {hasMore && (
              <Flex as="button" onClick={() => fetchPeriods(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Tạm giữ CCMG" right={<DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/chung-chi-moi-gioi`)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="#F6F8FC" minH="100dvh" pb="24px">
      <AppBar title="Tạm giữ CCMG" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/chung-chi-moi-gioi`)} />
      {pageBody}
    </Box>
  );
};

export default BdcHeld;
